import { useMemo, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useBestFitContextMenu } from './useBestFitContextMenu';
import { getBrokerColorClass } from './OrderBook';

interface TradeRow {
  time: string;
  board: string;
  price: number;
  quantity: number;
  buySell: string;
  buyerOrderNo: string;
  sellerOrderNo: string;
  brokerCodeBuyer: string;
  brokerTypeBuyer: string;
  brokerCodeSeller: string;
  brokerTypeSeller: string;
  timeValue: number;
}

export interface TradeHistoryRef {
  collapseAll: () => void;
}

export interface TradeHistoryProps {
  stockCode?: string;
  currentTime: number;
  allOrders: any[];
  isPlaying?: boolean;
  setCurrentTime?: React.Dispatch<React.SetStateAction<number>> | ((val: number | ((prev: number) => number)) => void);
  ignoreTimeFilter?: boolean;
  isGrouped?: boolean;
  showBroker?: boolean;
  showOrderNo?: boolean;
  prevClose?: number;
}

export const TradeHistory = forwardRef(function TradeHistory(
  {
    stockCode = '',
    currentTime,
    allOrders,
    isPlaying,
    setCurrentTime,
    ignoreTimeFilter,
    isGrouped = true,
    showBroker = true,
    showOrderNo = true,
    prevClose = 0
  }: TradeHistoryProps,
  ref: React.ForwardedRef<TradeHistoryRef>
) {
  const [sortConfig, setSortConfig] = useState<{ key: keyof TradeRow; direction: 'asc' | 'desc' } | null>({ key: 'timeValue', direction: 'desc' });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useImperativeHandle(ref, () => ({
    collapseAll: () => setExpandedGroups(new Set())
  }));
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const ignoreNextAutoScrollRef = useRef(false);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) setContainerHeight(containerRef.current.offsetHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    setScrollTop(target.scrollTop);

    // Detect if we scrolled near the bottom (within 40px)
    const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 40;
    if (isNearBottom && !isPlaying && setCurrentTime) {
      // Find all future matched trades
      const futureTrades = allOrders.filter(row =>
        (row.message === 'Full Matched' || row.message === 'Partial Matched') &&
        row.timeValue > currentTime &&
        row.lot > 0
      );
      if (futureTrades.length > 0) {
        // Take the 200th future trade (or the last one if fewer than 200 left)
        const targetTrade = futureTrades[Math.min(199, futureTrades.length - 1)];
        ignoreNextAutoScrollRef.current = true;
        setCurrentTime(targetTrade.timeValue);
      }
    }
  };

  const { isBestFit, handleContextMenu, ContextMenu } = useBestFitContextMenu();

  // Compute fit-content widths in px from full dataset.
  const colWidths = useMemo(() => {
    let maxPriceVal = 0, maxLot = 1, maxBuyer = 5, maxSeller = 5, maxOrd = 4, maxTime = 4, maxStock = 4, maxBType = 1, maxSType = 1;
    for (const o of allOrders) {
      if (o.message !== 'Full Matched' && o.message !== 'Partial Matched') continue;
      if (o.lot <= 0) continue;
      if (o.price > maxPriceVal) maxPriceVal = o.price;
      const lotLen    = o.lot   > 0 ? Math.round(o.lot).toLocaleString().length : 1;
      const buyerLen  = o.buyerBroker ? String(o.buyerBroker).length : 0;
      const sellerLen = o.sellerBroker ? String(o.sellerBroker).length : 0;
      const ordLen    = Math.max(o.buyerOrderNo ? String(o.buyerOrderNo).length : 0, o.sellerOrderNo ? String(o.sellerOrderNo).length : 0);
      const timeLen   = o.time ? String(o.time).length : 0;
      const stockLen  = stockCode ? stockCode.length : 4;
      const bTypeLen  = o.foreignDomesticBuyer ? String(o.foreignDomesticBuyer).length : 0;
      const sTypeLen  = o.foreignDomesticSeller ? String(o.foreignDomesticSeller).length : 0;
      
      if (lotLen    > maxLot)    maxLot    = lotLen;
      if (buyerLen  > maxBuyer)  maxBuyer  = buyerLen;
      if (sellerLen > maxSeller) maxSeller = sellerLen;
      if (ordLen    > maxOrd)    maxOrd    = ordLen;
      if (timeLen   > maxTime)   maxTime   = timeLen;
      if (stockLen  > maxStock)  maxStock  = stockLen;
      if (bTypeLen  > maxBType)  maxBType  = bTypeLen;
      if (sTypeLen  > maxSType)  maxSType  = sTypeLen;
    }
    const maxPrice = Math.max(maxPriceVal > 0 ? maxPriceVal.toLocaleString().length : 1, 6);
    const px = (n: number) => isBestFit ? `${Math.ceil(n * 8.5) + 12}px` : `${Math.ceil(n * 8.5) + 6}px`;
    
    return {
      price:  px(maxPrice),
      lot:    px(maxLot),
      buyer:  px(Math.max(maxBuyer, maxSeller)),
      seller: px(Math.max(maxBuyer, maxSeller)),
      ord:    `calc(${px(maxOrd)} + 13px)`,
      time:   isBestFit ? px(maxTime) : '70px',
      stock:  isBestFit ? px(maxStock) : '50px',
      bType:  isBestFit ? px(maxBType) : '22px',
      sType:  isBestFit ? px(maxSType) : '22px',
    };
  }, [allOrders, stockCode, isBestFit]);

  const baseData = useMemo(() => {
    return allOrders
      .filter(row => (row.message === 'Full Matched' || row.message === 'Partial Matched') && row.lot > 0)
      .map(order => ({
        time: order.time,
        board: order.board,
        price: order.price,
        quantity: order.lot,
        buySell: order.buySell,
        buyerOrderNo: String(order.buyerOrderNo ?? ''),
        sellerOrderNo: String(order.sellerOrderNo ?? ''),
        brokerCodeBuyer: order.buyerBroker,
        brokerTypeBuyer: order.foreignDomesticBuyer,
        brokerCodeSeller: order.sellerBroker,
        brokerTypeSeller: order.foreignDomesticSeller,
        timeValue: order.timeValue
      }));
  }, [allOrders]);

  const filtered = useMemo(() => {
    let matchedOrders: any[];
    let low = 0;
    let high = baseData.length - 1;
    let lastValidIndex = -1;
    
    while (low <= high) {
      const mid = (low + high) >> 1;
      if (baseData[mid].timeValue <= currentTime) {
        lastValidIndex = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    matchedOrders = baseData.slice(0, lastValidIndex + 1);
    if (matchedOrders.length > 5000) {
      matchedOrders = matchedOrders.slice(-5000);
    }
    
    let filtered = matchedOrders;

    if (sortConfig) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;

        if (sortConfig.key === 'time' || sortConfig.key === 'timeValue') {
          const aMaxOrd = Math.max(Number(a.buyerOrderNo) || 0, Number(a.sellerOrderNo) || 0);
          const bMaxOrd = Math.max(Number(b.buyerOrderNo) || 0, Number(b.sellerOrderNo) || 0);
          
          const aMinOrd = Math.min(Number(a.buyerOrderNo) || Infinity, Number(a.sellerOrderNo) || Infinity);
          const bMinOrd = Math.min(Number(b.buyerOrderNo) || Infinity, Number(b.sellerOrderNo) || Infinity);

          // 1. Sort by largest order number involved (the aggressor).
          // Larger aggressor = newer sweep.
          if (aMaxOrd !== bMaxOrd) return bMaxOrd - aMaxOrd;

          // 2. If same aggressor, sort by the passive order number.
          // Larger passive = matched later in the sweep.
          if (aMinOrd !== bMinOrd) return bMinOrd - aMinOrd;
        }

        // Tertiary sort: Chronological sequence
        return b._origIdx - a._origIdx;
      });
    } else {
      // Default: match data order but newest first
      filtered.reverse();
    }
    if (filtered.length > 5000) {
      filtered = filtered.slice(0, 5000);
    }
    return filtered;
  }, [allOrders, currentTime, sortConfig, ignoreTimeFilter]);

  const { flatData } = useMemo(() => {
    if (!isGrouped) {
      return { flatData: filtered };
    }

    const groups: any[] = [];
    let currentGroup: any = null;

    for (const trade of filtered) {
      if (!currentGroup) {
        currentGroup = {
          id: `${trade.timeValue}-${trade.buyerOrderNo}-${trade.sellerOrderNo}-${trade.price}-${groups.length}`,
          trades: [trade],
          totalQuantity: trade.quantity,
          isGroup: false,
          baseTrade: trade,
        };
        groups.push(currentGroup);
      } else {
        const base = currentGroup.baseTrade;
        const isSameBO = trade.buyerOrderNo === base.buyerOrderNo && trade.buyerOrderNo !== '';
        const isSameSO = trade.sellerOrderNo === base.sellerOrderNo && trade.sellerOrderNo !== '';
        
        let addToGroup = false;
        if (trade.timeValue === base.timeValue && trade.price === base.price) {
          if (!currentGroup.isGroup) {
            if (isSameBO || isSameSO) addToGroup = true;
          } else {
            if (currentGroup.varyingSide === 'S' && isSameBO) addToGroup = true;
            else if (currentGroup.varyingSide === 'B' && isSameSO) addToGroup = true;
          }
        }

        if (addToGroup) {
          currentGroup.trades.push(trade);
          currentGroup.totalQuantity += trade.quantity;
          if (!currentGroup.isGroup) {
            currentGroup.isGroup = true;
            currentGroup.varyingSide = isSameSO ? 'B' : 'S';
          }
        } else {
          currentGroup = {
            id: `${trade.timeValue}-${trade.buyerOrderNo}-${trade.sellerOrderNo}-${trade.price}-${groups.length}`,
            trades: [trade],
            totalQuantity: trade.quantity,
            isGroup: false,
            baseTrade: trade,
          };
          groups.push(currentGroup);
        }
      }
    }

    const flat: any[] = [];
    for (const g of groups) {
      const isExpanded = expandedGroups.has(g.id);
      flat.push({
        ...g.baseTrade,
        _isGroupHeader: g.isGroup,
        _groupId: g.id,
        _isExpanded: isExpanded,
        _totalQuantity: g.totalQuantity,
        _tradesCount: g.trades.length,
        _varyingSide: g.varyingSide,
      });
      if (g.isGroup && isExpanded) {
        for (const t of g.trades) {
          flat.push({
            ...t,
            _isDetail: true,
            _groupId: g.id,
          });
        }
      }
    }

    return { flatData: flat };
  }, [filtered, expandedGroups, isGrouped]);

  const visibleData = flatData;

  const rowHeight = 21;
  const totalHeight = visibleData.length * rowHeight;
  const contentHeight = totalHeight;

  // Auto-scroll: keep at top to see newest data
  useEffect(() => {
    if (ignoreNextAutoScrollRef.current) {
      ignoreNextAutoScrollRef.current = false;
      return;
    }
    const el = containerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      containerRef.current.scrollTop = 0;
      setScrollTop(0);
    });
  }, [currentTime]);
  
  // Memory management: render exactly 400 rows max around the viewport
  const renderCount = 400;
  const currentIndex = Math.floor(scrollTop / rowHeight);
  let startIndex = Math.max(0, currentIndex - Math.floor(renderCount / 2));
  let endIndex = Math.min(visibleData.length, startIndex + renderCount);
  
  if (endIndex - startIndex < renderCount && visibleData.length > renderCount) {
    startIndex = endIndex - renderCount;
  }

  const handleSort = (key: keyof TradeRow) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  return (
    <div className={`flex flex-col h-full bg-black border-t border-gray-800 relative ${isBestFit ? 'overflow-x-auto' : 'overflow-hidden'}`}>
      <ContextMenu />
      <div className={`flex bg-gray-900 text-gray-400 text-[13px] font-bold border-b border-gray-700 cursor-pointer select-none py-0.5 z-10 pr-[6px] whitespace-nowrap ${isBestFit ? 'w-full' : ''}`} onContextMenu={handleContextMenu}>
        <div onClick={() => handleSort('time')} className="px-1 text-center flex items-center justify-center gap-0.5" style={{width: colWidths.time}}>
          Time<span className="text-[10px]">{sortConfig?.key === 'time' ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : <span className="text-gray-600"> ↕</span>}</span>
        </div>
        <div className="px-1 text-center" style={{width: colWidths.stock}}>Stock</div>
        <div onClick={() => handleSort('price')} className="px-1 text-center flex items-center justify-center gap-0.5" style={{width: colWidths.price}}>
          Price<span className="text-[10px]">{sortConfig?.key === 'price' ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : <span className="text-gray-600"> ↕</span>}</span>
        </div>
        <div onClick={() => handleSort('quantity')} className="px-1 text-center" style={{width: colWidths.lot}}>Lot</div>
        {showBroker && <div onClick={() => handleSort('brokerCodeBuyer')} className="px-1 flex items-center justify-center shrink-0" style={{width: `calc(${colWidths.bType} + ${colWidths.buyer})`}}>Buyer</div>}
        {showBroker && <div onClick={() => handleSort('brokerCodeSeller')} className="px-1 flex items-center justify-center shrink-0" style={{width: `calc(${colWidths.seller} + ${colWidths.sType})`}}>Seller</div>}
        {showOrderNo && <div onClick={() => handleSort('buyerOrderNo')} className="px-1 flex items-center justify-center shrink-0" style={{width: colWidths.ord}}>BO No</div>}
        {showOrderNo && <div onClick={() => handleSort('sellerOrderNo')} className="px-1 flex items-center justify-center shrink-0" style={{width: colWidths.ord}}>SO No</div>}
        {isBestFit && <div className="flex-1"></div>}
      </div>

      <div ref={containerRef} className={`flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 relative ${isBestFit ? 'w-full' : ''}`} onScroll={handleScroll}>
        <div style={{ height: contentHeight, width: '100%', willChange: 'transform' }}>
          <div style={{ transform: `translate3d(0, ${startIndex * rowHeight}px, 0)`, willChange: 'transform' }}>
            {visibleData.slice(startIndex, endIndex).map((tradeItem: any, idx) => {
              const trade = tradeItem;
              const boNum = Number(trade.buyerOrderNo) || Infinity;
              const soNum = Number(trade.sellerOrderNo) || Infinity;
              const isBuyerPassive = boNum < soNum;
              const isSellerPassive = soNum < boNum;
              const buyerBg = isBuyerPassive ? 'bg-gray-800' : '';
              const sellerBg = isSellerPassive ? 'bg-gray-800' : '';

              const globalIdx = startIndex + idx;

              // We always show order numbers unless it's replaced by a group toggle
              const showBO = true;
              const showSO = true;

              const isGroupHeader = trade._isGroupHeader;
              const isExpanded = trade._isExpanded;
              const isDetail = trade._isDetail;
              const rowBg = isDetail ? 'bg-gray-800/40' : 'hover:bg-gray-900';
              const varyingSide = trade._varyingSide;

              const toggleButton = (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedGroups(prev => {
                      const next = new Set(prev);
                      if (next.has(trade._groupId)) next.delete(trade._groupId);
                      else next.add(trade._groupId);
                      return next;
                    });
                  }}
                  className="w-[18px] h-[18px] flex items-center justify-center bg-black border border-gray-500 hover:bg-gray-800 rounded-sm text-[11px] text-white font-mono leading-none font-bold mx-auto"
                  title="Show/Hide Details"
                >
                  {isExpanded ? '-' : '+'}
                </button>
              );

              return (
                <div key={globalIdx} className={`flex ${rowBg} text-[13px] tabular-nums items-stretch h-[21px] overflow-hidden`}>
                  <div className="px-1 flex items-center justify-center text-gray-400 truncate" style={{width: colWidths.time}}>
                    {trade.time}
                  </div>
                  <div className="px-1 flex items-center justify-center text-gray-400 truncate" style={{width: colWidths.stock}}>{stockCode}</div>
                  <div className="px-1 flex items-center justify-end truncate" style={{width: colWidths.price, color: prevClose > 0 ? (trade.price > prevClose ? '#22c55e' : trade.price < prevClose ? '#ef4444' : '#eab308') : '#d1d5db'}}>{trade.price.toLocaleString()}</div>
                  <div className="px-1 flex items-center justify-end truncate" style={{width: colWidths.lot, color: trade.buySell === 'B' ? '#22c55e' : '#ef4444'}}>
                    {(isGroupHeader ? trade._totalQuantity : trade.quantity).toLocaleString()}
                  </div>
                  {showBroker && <div className={`px-1 flex items-center justify-end text-gray-400 truncate ${buyerBg}`} style={{width: colWidths.bType}}>{isGroupHeader && varyingSide === 'B' ? '' : trade.brokerTypeBuyer}</div>}
                  {showBroker && <div className={`px-1 flex items-center justify-center truncate ${buyerBg} ${getBrokerColorClass(trade.brokerCodeBuyer)}`} style={{width: colWidths.buyer}}>
                    {isGroupHeader && varyingSide === 'B' ? toggleButton : trade.brokerCodeBuyer}
                  </div>}
                  {showBroker && <div className={`px-1 flex items-center justify-center truncate ${sellerBg} ${getBrokerColorClass(trade.brokerCodeSeller)}`} style={{width: colWidths.seller}}>
                    {isGroupHeader && varyingSide === 'S' ? toggleButton : trade.brokerCodeSeller}
                  </div>}
                  {showBroker && <div className={`px-1 flex items-center justify-start text-gray-400 truncate ${sellerBg}`} style={{width: colWidths.sType}}>{isGroupHeader && varyingSide === 'S' ? '' : trade.brokerTypeSeller}</div>}
                  {showOrderNo && <div className={`px-1 flex items-center justify-center text-gray-400 truncate ${buyerBg}`} style={{width: colWidths.ord}}>
                    {isGroupHeader && varyingSide === 'B' ? '' : (showBO ? trade.buyerOrderNo : '')}
                  </div>}
                  {showOrderNo && <div className={`px-1 flex items-center justify-center text-gray-500 truncate ${sellerBg}`} style={{width: colWidths.ord}}>
                    {isGroupHeader && varyingSide === 'S' ? '' : (showSO ? trade.sellerOrderNo : '')}
                  </div>}
                  {isBestFit && <div className="flex-1"></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});


